import Modal from '@ui/modal/modal';
import Button from '@ui/buttons/button/button';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import { Dispatch, useEffect, useState } from 'react';
// import SettingsToggle from '../../../settings/controls/settings-toggle';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import OptionalComponent from '@ui/optional-component/optional-component';
// import Popover from '@ui/popover/popover';
// import DateRangePicker from '@ui/calendar/date-range-picker';
// import { niceDateTimeTz } from '@/utils/dates';
import UploadLarge from '@ui/uploads/upload-large';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import { allListMembersValid, GenerateExampleCsv, getDefaultPasscodeList } from '../../../utils';
import { PASSCODE_LIST_TYPE, PasscodeList } from '@/types/registration.types';
import { useAppSelector } from '@/store/hooks';
import { getWorkingProjectUuid } from '@/store/slices/project/selectors';
import {
  GetUploadEndpoint,
  getUploadFilename,
  SetItemContentType,
  UploadFileToProvider,
} from '@ui/uploads/validations';
import { isObject } from 'underscore';
import { WorkingPasscodeList } from '@/types/passcode-list.types';
import toastTrigger from '@ui/toast/toast-trigger';
import ConfirmUploadRegistrantsModal from './confirm-upload-registrants-modal';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

type ManualInviteRowData = {
  email: string;
  firstName: string;
  lastName: string;
};

type UploadRegistrantsModalProps = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  onSave: (workingPasscodeList: WorkingPasscodeList) => Promise<void>;
  saving: boolean;
  passcodeList?: PasscodeList;
  onSaveComplete?: () => void;
};

// TODO Matt: commented code left in on purpose for future work, which includes:
// scheduling of email notifications
// select list(s) from other project(s)

const UploadRegistrantsModal = ({
  open,
  setOpen,
  onSave,
  saving,
  passcodeList,
  onSaveComplete,
}: UploadRegistrantsModalProps) => {
  const projectUuid = useAppSelector(getWorkingProjectUuid);

  // const [scheduleEmail, setScheduleEmail] = useState(false);
  const [bulkUpload] = useState(true);
  // const [scheduleDate, setScheduleDate] = useState<number | null>(null);
  const [rows, setRows] = useState<ManualInviteRowData[]>([{ email: '', firstName: '', lastName: '' }]);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [workingPasscodeList, setWorkingPasscodeList] = useState<WorkingPasscodeList>(() =>
    getDefaultPasscodeList(projectUuid || '', true)
  );
  const [testEmailAddressesForSend, setTestEmailAddressesForSend] = useState<string[]>([]);
  const [sendTestEmailModalOpen, setSendTestEmailModalOpen] = useState(false);

  // const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  useEffect(() => {
    const manuallyAddedRows = rows
      .filter((row) => row.email.trim())
      .map((row) => ({
        email: row.email,
        first_name: row.firstName,
        last_name: row.lastName,
        passcode: '',
      }));

    setWorkingPasscodeList((list) => ({
      ...list,
      manually_added_users: manuallyAddedRows,
    }));
  }, [rows]);

  const handleClose = () => {
    setOpen(false);
    setRows([{ email: '', firstName: '', lastName: '' }]);
    setFileName(null);
    setSendTestEmailModalOpen(false);
    setTestEmailAddressesForSend([]);
  };

  const onFile = async (file: File | FileList) => {
    if (file instanceof File) {
      setLoading(true);
      setFileName(file.name);
      try {
        const endpoint = await GetUploadEndpoint({
          filename: getUploadFilename(file.name),
        });

        if (isObject(endpoint) && endpoint.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );
          SetItemContentType({ filename: finalUrl, contentType: file.type }).catch(console.error);

          setWorkingPasscodeList((list) => ({ ...list, url: finalUrl }));
        }
      } catch (e) {
        console.error(e);
        toastTrigger(
          <div>
            <h6>Error attaching file</h6>
            <p>There was an error attaching the file.</p>
          </div>,
          { type: 'error' }
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    const listToSave = {
      ...workingPasscodeList,
      fileName: fileName || undefined,
      ...(passcodeList?.type != null && { type: passcodeList.type }),
    };

    // merge test email addresses if any
    if (testEmailAddressesForSend.length > 0) {
      const testUsers = testEmailAddressesForSend.map((email) => ({
        email,
        first_name: 'Test',
        last_name: 'Email',
        passcode: '',
      }));
      listToSave.manually_added_users = [...(listToSave.manually_added_users || []), ...testUsers];
    }

    await onSave(listToSave);

    onSaveComplete?.();
    handleClose();
  };

  const isSaveDisabled = saving || !allListMembersValid(workingPasscodeList);

  return (
    <>
      <Modal
        open={open}
        onRequestClose={handleClose}
        customSize={{
          width: '720px',
          height: 'auto',
          maxHeight: '800px',
          maxWidth: 'calc(100% - 32px)',
        }}
        contentWrapperClassName="padding-24 overflow-y-auto flex-1 flex-column"
        backgroundColor="var(--gray-900)"
        renderHeaderContent={() => (
          <Container className="flex-column gap-8 padding-b-16" borderRadius="16px">
            <Text className="H4">Upload Registrants</Text>
            <Text className="P1" color="var(--gray-500)">
              Create registrations for targeted invitees. Project must be published first.
            </Text>
          </Container>
        )}
        renderFooterContent={() => (
          <Container background="var(--gray-900)" className="flex fd-row jc-flex-end ai-flex-end gap-16">
            <Button
              design={TButtonAndLinkDesigns.LocalBttnSecHalfTransp}
              onClick={handleClose}
              className="L1 padding-v-16 padding-h-24"
            >
              <Text className="L1">Cancel</Text>
            </Button>
            <Button
              onClick={() => {
                if (!passcodeList) {
                  setSendTestEmailModalOpen(true);
                } else {
                  handleSave();
                }
              }}
              className="padding-v-16 padding-h-24"
              disabled={isSaveDisabled}
              disabledOpacity={0.6}
              backgroundColor={getThemeColorCSSVariable(EColorVariablesV2.primary)}
            >
              <Text
                className="L1"
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
              >
                Save
              </Text>
            </Button>
          </Container>
        )}
      >
        <Container className="flex-column gap-16 padding-16" minHeight="190px">
          {/* <Container className="flex-column gap-16 padding-16"> */}
          {/* <SettingsToggle
              offLabel="From project"
              onLabel="Upload list"
              fullWidth
              value={bulkUpload}
              sKey="sendEmailOnInvite"
              onToggle={(_, value) => setBulkUpload(value)}
              padding="0"
            />
            <OptionalComponent display={!bulkUpload}>
              <Container className="padding-l-16">
                <Text className="L3" color="var(--gray-500)">
                  Select Project
                </Text>
              </Container>
            </OptionalComponent> */}
          <OptionalComponent display={bulkUpload}>
            <UploadLarge
              onFile={onFile}
              allowedFileTypes={['text/csv']}
              style={{
                height: '88px',
                minHeight: '88px',
                background: 'transparent',
                border: '1px dashed var(--gray-600)',
              }}
              uploading={loading}
              placeholder=""
              CustomTitle={
                <>
                  <OptionalComponent display={!fileName}>
                    <Container
                      className="flex fd-column jc-center ai-center"
                      height="150px"
                      style={{
                        width: 'fit-content',
                        margin: 'auto',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        padding: '8px',
                      }}
                    >
                      <Container className="L2 flex fd-row jc-flex-start ai-center gap-8">
                        <Icon
                          name={ICONS.UPLOAD}
                          size={12}
                          color={getThemeColorCSSVariable(EColorVariablesV2.primary)}
                        />
                        <Text className="S2" color={getThemeColorCSSVariable(EColorVariablesV2.primary)}>
                          Upload registrants
                        </Text>
                      </Container>
                      <Text className="padding-t-4" fontSize="12px">
                        or drag and drop here file in .csv file format
                      </Text>
                    </Container>
                  </OptionalComponent>
                  <OptionalComponent display={!!fileName}>
                    <Text>{fileName}</Text>
                  </OptionalComponent>
                </>
              }
            />
            <Container className="flex ai-center jc-space-between">
              <Text className="C1">Magic Links require uploading via template</Text>
              <Button
                design={TButtonAndLinkDesigns.LocalBttnSecHalfTransp}
                width="fit-content"
                onClick={() =>
                  GenerateExampleCsv(passcodeList?.type || PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION, projectUuid)
                }
                className="flex ai-center gap-6"
                backgroundColor="var(--gray-700)"
              >
                <Icon name={ICONS.DOWNLOAD} size={12} />
                <Text className="L1">Download template</Text>
              </Button>
            </Container>
          </OptionalComponent>
          {/* </Container> */}
        </Container>
        {/* <OptionalComponent display={!passcodeList}>
          <Container className="flex fd-column" minHeight="160px">
            <Text className="L3 padding-b-16" color="var(--gray-500)">
              Send Email
            </Text>
            <Container className="flex-column gap-16 padding-h-16">
              <SettingsToggle
                offLabel="Now"
                onLabel="In the future"
                fullWidth
                value={scheduleEmail}
                sKey="sendEmailOnInvite"
                onToggle={(_, value) => setScheduleEmail(value)}
                padding="0"
              />
              <OptionalComponent display={scheduleEmail}>
                <Container className="flex-1" minWidth="308px" width="100%" maxWidth="100%">
                  <Popover
                    forcePosition="top-center"
                    width="fit-content"
                    contentClassName="margin-t--4 overflow-hidden"
                    renderTriggerButton={() => (
                      <button className="flex fd-row jc-space-between ai-center radius-12 width-100-percent">
                        <div className="text-left">
                          <Text className="C3 margin-b-6">Scheduled time</Text>
                          <div>
                            <Text className="S2">{niceDateTimeTz(Number(scheduleDate || Date.now()))}</Text>
                          </div>
                        </div>
                        <div>
                          <Icon name={ICONS.CALENDAR} />
                        </div>
                      </button>
                    )}
                  >
                    <DateRangePicker
                      selectSingleDate
                      hideTimezone
                      onChange={(date) => {
                        setScheduleDate(date.startDate);
                      }}
                      defaultValue={{
                        startDate: scheduleDate || Number(Date.now()),
                        endDate: null,
                        selectedTimezone: userTz,
                      }}
                    />
                  </Popover>
                </Container>
              </OptionalComponent>
            </Container>
          </Container>
        </OptionalComponent> */}
      </Modal>
      <ConfirmUploadRegistrantsModal
        open={sendTestEmailModalOpen}
        setOpen={setSendTestEmailModalOpen}
        setTestEmailAddressesForSend={setTestEmailAddressesForSend}
        sendEmails={handleSave}
        isSaving={saving}
        saveDisabled={isSaveDisabled}
      />
    </>
  );
};

export default UploadRegistrantsModal;
